import React, { useState, useEffect, forwardRef } from 'react';
import { Group, Rect, Text } from 'react-konva';
import { KonvaEventObject, Node } from 'konva/lib/Node';
import Konva from 'konva'; // Import Konva for type usage
import {
  AdvancedTableElementProps,
  AdvancedTableCellData,
  AdvancedTableHeader,
  AdvancedTableData,
} from '../../../types';
import { formatCurrency } from '../../../helpers';

const MIN_COLUMN_WIDTH = 20; // Minimum width for a column
const MIN_ROW_HEIGHT = 20; // Minimum height for a row

// List of advanced table column keys to be treated as money fields
const TABLE_MONEY_COLUMN_KEYS = [
  'cityTaxAmount',
  'cost',
  'countyTaxAmount',
  'discountTotal',
  'discount',
  'total',
  'multiCurrencyDiscountTotal',
  'multiCurrencyItemPrice',
  'multiCurrencySubTotal',
  'multiCurrencyTaxTotal',
  'multiCurrencyTotal',
  'price',
  'specialDistrictTaxAmount',
  'stateTaxAmount',
  'subTotal',
  'tax',
];

// Helper function to determine display text for advanced table cells
const getAdvancedTableCellDisplayValue = (
  cellValue: any,
  columnKey: string | undefined,
  previewMode: boolean
): string => {
  if (previewMode) {
    if (columnKey && TABLE_MONEY_COLUMN_KEYS.includes(columnKey)) {
      return formatCurrency(cellValue);
    }
    // For non-money columns in preview mode, or if columnKey is undefined
    if (
      cellValue === null ||
      cellValue === undefined ||
      String(cellValue).trim() === ''
    ) {
      return '';
    }
    return String(cellValue);
  } else {
    // Not preview mode - show actual data or blank
    if (
      cellValue === null ||
      cellValue === undefined ||
      String(cellValue).trim() === ''
    ) {
      return '';
    }
    return String(cellValue);
  }
};

const AdvancedTableElement = forwardRef<
  Konva.Group, // Type of the ref (Konva.Group)
  AdvancedTableElementProps
>((props, ref) => {
  const {
    x,
    y,
    advancedTableData,
    id,
    onClick,
    onTap,
    draggable,
    onMouseEnter, // Destructure onMouseEnter
    onMouseLeave, // Destructure onMouseLeave
    previewMode = false,
  } = props;

  if (!advancedTableData) {
    return null;
  }

  const {
    numRows,
    numColumns,
    columnWidths: initialColumnWidths,
    rowHeights: initialRowHeights,
    data,
    headerRow,
    headerRowHeight = 30,
    headers,
    headerFontFamily = 'Arial',
    headerFontSize = 14,
    headerBackgroundColor = '#f0f0f0',
    headerTextColor = '#000000',
    defaultCellFontFamily = 'Arial',
    defaultCellFontSize = 12,
    defaultCellBackgroundColor = '#ffffff',
    defaultCellTextColor = '#333333',
    borderColor = '#cccccc',
    borderWidth = 1,
    showGrid = true,
  } = advancedTableData;

  const [localColumnWidths, setLocalColumnWidths] =
    useState(initialColumnWidths);
  const [localRowHeights, setLocalRowHeights] = useState(initialRowHeights);

  // State for column drag operations
  const [draggingColumnIndex, setDraggingColumnIndex] = useState<number | null>(
    null
  );
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [initialDragWidths, setInitialDragWidths] = useState<number[] | null>(
    null
  );

  // State for row drag operations
  const [draggingRowIndex, setDraggingRowIndex] = useState<number | null>(null);
  const [dragStartY, setDragStartY] = useState<number | null>(null);
  const [initialDragHeights, setInitialDragHeights] = useState<number[] | null>(
    null
  );

  const [isResizing, setIsResizing] = useState(false);

  const handleMainTableDragEnd = (e: KonvaEventObject<DragEvent>) => {
    // Only call the passed onDragEnd (for grid snapping) if:
    // 1. We are not in resizing mode (as a safeguard, though draggable should prevent this)
    // 2. The drag event target is the group itself, not a child handle
    if (!isResizing && e.target === e.currentTarget) {
      props.onDragEnd?.(e); // Call the original onDragEnd from ElementRenderer
    }
    // If it was a resize handle drag, its own onDragEnd would have handled setIsResizing(false)
    // and other logic. We don't want to snap the table based on a handle's drag end.
  };

  useEffect(() => {
    setLocalColumnWidths(initialColumnWidths);
    setLocalRowHeights(initialRowHeights);
  }, [advancedTableData]);

  const cumulativeColX: number[] = [0];

  localColumnWidths.reduce((acc, currentWidth) => {
    const nextX = acc + currentWidth;
    cumulativeColX.push(nextX);
    return nextX;
  }, 0);

  const headerActualHeight = headerRow ? headerRowHeight : 0;
  const cumulativeRowY: number[] = [headerActualHeight];
  localRowHeights.reduce((acc, currentHeight) => {
    const nextY = acc + currentHeight;
    cumulativeRowY.push(nextY);
    return nextY;
  }, headerActualHeight);

  const totalCalculatedWidth =
    cumulativeColX[cumulativeColX.length - 1] || props.width || 0;
  const totalCalculatedHeight =
    cumulativeRowY[cumulativeRowY.length - 1] || props.height || 0;

  const resizeHandleWidth = 6;
  const resizeHandleHeight = 6;

  const handleColumnDragStart = (
    columnIndex: number,
    e: KonvaEventObject<DragEvent>
  ) => {
    setIsResizing(true);
    e.evt.stopPropagation(); // PREVENT PARENT DRAG
    e.evt.preventDefault();
    const handleNode = e.target;
    const group = handleNode.getParent();
    if (!group) return;

    const pointerPos = group.getRelativePointerPosition(); // Get pos relative to group
    if (!pointerPos) return;

    setDraggingColumnIndex(columnIndex);
    setDragStartX(pointerPos.x); // Store X relative to group
    setInitialDragWidths([...localColumnWidths]);
  };

  const handleColumnDragMove = (
    columnIndex: number,
    e: KonvaEventObject<DragEvent>
  ) => {
    // e.evt.stopPropagation(); // Optional: usually not needed for move if start is stopped
    e.evt.preventDefault();
    const handleNode = e.target;
    const group = handleNode.getParent();

    if (
      draggingColumnIndex === null ||
      dragStartX === null ||
      !initialDragWidths ||
      !group
    ) {
      return;
    }

    const pointerPos = group.getRelativePointerPosition(); // Get pos relative to group
    if (!pointerPos) return;

    const dx = pointerPos.x - dragStartX; // dx is now in group's local coordinates
    let currentDeltaX = dx;
    const newColumnWidths = [...initialDragWidths];

    // Clamp deltaX: current column cannot shrink below MIN_COLUMN_WIDTH
    const maxNegativeDeltaX = -(
      initialDragWidths[draggingColumnIndex] - MIN_COLUMN_WIDTH
    );
    currentDeltaX = Math.max(maxNegativeDeltaX, currentDeltaX);

    // Clamp deltaX: next column cannot shrink below MIN_COLUMN_WIDTH (if it exists)
    if (draggingColumnIndex + 1 < numColumns) {
      const maxPositiveDeltaX =
        initialDragWidths[draggingColumnIndex + 1] - MIN_COLUMN_WIDTH;
      currentDeltaX = Math.min(maxPositiveDeltaX, currentDeltaX);
    }

    newColumnWidths[draggingColumnIndex] =
      initialDragWidths[draggingColumnIndex] + currentDeltaX;

    if (draggingColumnIndex + 1 < numColumns) {
      newColumnWidths[draggingColumnIndex + 1] =
        initialDragWidths[draggingColumnIndex + 1] - currentDeltaX;
    }

    setLocalColumnWidths(newColumnWidths);
  };

  const handleColumnDragEnd = (
    columnIndex: number,
    event: KonvaEventObject<DragEvent>
  ) => {
    setIsResizing(false);
    event.evt.stopPropagation(); // Already here, good.
    if (
      draggingColumnIndex !== null &&
      props.onUpdateElement &&
      advancedTableData
    ) {
      event.evt.stopPropagation();
      const newTotalWidth = localColumnWidths.reduce((sum, w) => sum + w, 0);

      const newAdvancedTableData: AdvancedTableData = {
        ...advancedTableData,
        columnWidths: [...localColumnWidths],
      };

      const updatePayload: Partial<AdvancedTableElementProps> = {
        id: props.id,
        advancedTableData: newAdvancedTableData,
        width: newTotalWidth,
      };

      props.onUpdateElement(updatePayload);
    }
    setDraggingColumnIndex(null);
    setDragStartX(null);
    setInitialDragWidths(null);
  };

  const handleRowDragStart = (
    rowIndex: number,
    e: KonvaEventObject<DragEvent>
  ) => {
    setIsResizing(true);
    e.evt.stopPropagation(); // PREVENT PARENT DRAG
    e.evt.preventDefault();
    const handleNode = e.target;
    const group = handleNode.getParent();
    if (!group) return;

    const pointerPos = group.getRelativePointerPosition(); // Get pos relative to group
    if (!pointerPos) return;

    setDraggingRowIndex(rowIndex);
    setDragStartY(pointerPos.y); // Store Y relative to group
    setInitialDragHeights([...localRowHeights]);
  };

  const handleRowDragMove = (
    rowIndex: number,
    e: KonvaEventObject<DragEvent>
  ) => {
    // e.evt.stopPropagation(); // Optional: usually not needed for move if start is stopped
    e.evt.preventDefault();
    const handleNode = e.target;
    const group = handleNode.getParent();

    if (
      draggingRowIndex === null ||
      dragStartY === null ||
      !initialDragHeights ||
      !group
    ) {
      return;
    }

    const pointerPos = group.getRelativePointerPosition(); // Get pos relative to group
    if (!pointerPos) return;

    const dy = pointerPos.y - dragStartY; // dy is now in group's local coordinates
    let currentDeltaY = dy;
    const newRowHeights = [...initialDragHeights];

    // Clamp deltaY: current row cannot shrink below MIN_ROW_HEIGHT
    const maxNegativeDeltaY = -(
      initialDragHeights[draggingRowIndex] - MIN_ROW_HEIGHT
    );
    currentDeltaY = Math.max(maxNegativeDeltaY, currentDeltaY);

    // Clamp deltaY: next row cannot shrink below MIN_ROW_HEIGHT (if it exists)
    if (draggingRowIndex + 1 < numRows) {
      const maxPositiveDeltaY =
        initialDragHeights[draggingRowIndex + 1] - MIN_ROW_HEIGHT;
      currentDeltaY = Math.min(maxPositiveDeltaY, currentDeltaY);
    }

    newRowHeights[draggingRowIndex] =
      initialDragHeights[draggingRowIndex] + currentDeltaY;

    if (draggingRowIndex + 1 < numRows) {
      newRowHeights[draggingRowIndex + 1] =
        initialDragHeights[draggingRowIndex + 1] - currentDeltaY;
    }

    setLocalRowHeights(newRowHeights);
  };

  const handleRowDragEnd = (
    rowIndex: number,
    event: KonvaEventObject<DragEvent>
  ) => {
    setIsResizing(false);
    event.evt.stopPropagation(); // Already here, good.
    if (
      draggingRowIndex !== null &&
      props.onUpdateElement &&
      advancedTableData
    ) {
      event.evt.stopPropagation();
      const headerActualHeight = advancedTableData.headerRow
        ? advancedTableData.headerRowHeight || 30
        : 0;
      const newTotalHeight =
        localRowHeights.reduce((sum, h) => sum + h, 0) + headerActualHeight;

      const newAdvancedTableData: AdvancedTableData = {
        ...advancedTableData,
        rowHeights: [...localRowHeights],
      };

      const updatePayload: Partial<AdvancedTableElementProps> = {
        id: props.id,
        advancedTableData: newAdvancedTableData,
        height: newTotalHeight,
      };

      props.onUpdateElement(updatePayload);
    }
    setDraggingRowIndex(null);
    setDragStartY(null);
    setInitialDragHeights(null);
  };

  return (
    <Group
      ref={ref} // Use the forwarded ref here
      x={x}
      y={y}
      id={id}
      draggable={!isResizing && draggable} // Conditionally draggable
      onClick={onClick}
      onTap={onTap}
      onDragEnd={handleMainTableDragEnd} // Use the new wrapper
      onMouseEnter={onMouseEnter} // Pass onMouseEnter
      onMouseLeave={onMouseLeave} // Pass onMouseLeave
    >
      {/* Background for the entire table */}
      <Rect
        width={totalCalculatedWidth}
        height={totalCalculatedHeight}
        fill={defaultCellBackgroundColor}
      />

      {/* Render Header Row */}
      {headerRow && headers && headers.length > 0 && (
        <Group y={0}>
          {headers.map((header: AdvancedTableHeader, colIndex) => {
            const headerCellX = cumulativeColX[colIndex];
            const headerCellWidth = localColumnWidths[colIndex];

            return (
              <React.Fragment key={`header-${header.key}`}>
                <Rect
                  x={headerCellX}
                  y={0}
                  width={headerCellWidth}
                  height={headerRowHeight}
                  fill={headerBackgroundColor}
                  stroke={showGrid ? borderColor : undefined}
                  strokeWidth={showGrid ? borderWidth : 0}
                />
                <Text
                  x={headerCellX + 5}
                  y={5}
                  width={headerCellWidth - 10}
                  height={headerRowHeight - 10}
                  text={header.label}
                  fontFamily={headerFontFamily}
                  fontSize={headerFontSize}
                  fill={headerTextColor}
                  verticalAlign="middle"
                />
              </React.Fragment>
            );
          })}
        </Group>
      )}

      {/* Render Data Cells */}
      {data.map((rowData, rowIndex) => {
        const cellY = cumulativeRowY[rowIndex] || 0;
        const cellRowHeight = localRowHeights[rowIndex] || 18;

        return (
          <Group key={`row-${rowIndex}`} y={cellY}>
            {rowData.map((cell: AdvancedTableCellData | null, colIndex) => {
              const cellX = cumulativeColX[colIndex] || 0;
              const cellWidth = localColumnWidths[colIndex] || 18;

              const columnKey = headers?.[colIndex]?.key;
              const cellValue = cell?.content;
              const displayText = getAdvancedTableCellDisplayValue(
                cellValue,
                columnKey,
                previewMode
              );

              return (
                <React.Fragment key={`cell-${rowIndex}-${colIndex}`}>
                  <Rect
                    x={cellX}
                    y={0}
                    width={cellWidth}
                    height={cellRowHeight}
                    fill={defaultCellBackgroundColor}
                    stroke={showGrid ? borderColor : undefined}
                    strokeWidth={showGrid ? borderWidth : 0}
                  />
                  {/* Always render Text, as displayText handles empty/zero cases */}
                  <Text
                    x={cellX + 5}
                    y={5}
                    width={cellWidth - 10}
                    height={cellRowHeight - 10}
                    text={displayText} // Use the formatted or original text
                    fontFamily={defaultCellFontFamily}
                    fontSize={defaultCellFontSize}
                    fill={defaultCellTextColor}
                    verticalAlign="middle"
                  />
                </React.Fragment>
              );
            })}
          </Group>
        );
      })}

      {/* Column Resize Handles */}
      {!previewMode &&
        Array.from({ length: numColumns - 1 }).map((_, index) => {
          const handleXPos = cumulativeColX[index + 1] - resizeHandleWidth / 2;
          return (
            <Rect
              key={`col-resize-handle-${index}`}
              x={handleXPos}
              y={0}
              width={resizeHandleWidth}
              height={totalCalculatedHeight}
              fill={
                draggingColumnIndex === index
                  ? 'rgba(0, 100, 255, 0.3)'
                  : 'rgba(0, 0, 255, 0.01)'
              }
              draggable
              dragBoundFunc={function (this: Node, pos) {
                const parentNode = this.parent;
                if (!parentNode) return pos;

                const parentAbsolutePos = parentNode.getAbsolutePosition();
                const parentScaleX = parentNode.getAbsoluteScale().x;
                const handleLocalWidth = this.width(); // Local, unscaled width of the handle

                // Min/Max stage X for the handle's LEFT EDGE based on column minimum widths
                // Handle 'index' is between column 'index' and 'index+1'
                // The handle's center is conceptually at cumulativeColX[index + 1]
                // Its local left edge is at cumulativeColX[index + 1] - handleLocalWidth / 2

                // Constraint from left column (column 'index')
                // Leftmost position for (cumulativeColX[index + 1]) is (cumulativeColX[index] + MIN_COLUMN_WIDTH)
                const minHandleStageX_dueToCols =
                  parentAbsolutePos.x +
                  (cumulativeColX[index] +
                    MIN_COLUMN_WIDTH -
                    handleLocalWidth / 2) *
                    parentScaleX;

                // Constraint from right column (column 'index+1')
                // Rightmost position for (cumulativeColX[index + 1]) is (cumulativeColX[index+2] - MIN_COLUMN_WIDTH)
                const maxHandleStageX_dueToCols =
                  parentAbsolutePos.x +
                  (cumulativeColX[index + 2] -
                    MIN_COLUMN_WIDTH -
                    handleLocalWidth / 2) *
                    parentScaleX;

                // Clamp proposed pos.x by column constraints first
                let newX = Math.max(minHandleStageX_dueToCols, pos.x);
                newX = Math.min(newX, maxHandleStageX_dueToCols);

                // Overall table boundaries for the handle's LEFT EDGE
                const overallMinHandleStageX = parentAbsolutePos.x; // Cannot go left of table's left edge
                const overallMaxHandleStageX =
                  parentAbsolutePos.x +
                  (totalCalculatedWidth - handleLocalWidth) * parentScaleX; // Cannot go right of table's right edge

                // Further clamp by overall table boundaries
                newX = Math.max(overallMinHandleStageX, newX);
                newX = Math.min(newX, overallMaxHandleStageX);

                return {
                  x: newX,
                  y: parentAbsolutePos.y, // Lock Y to parent's absolute y
                };
              }}
              onDragStart={(e) => handleColumnDragStart(index, e)}
              onDragMove={(e) => handleColumnDragMove(index, e)}
              onDragEnd={(e) => handleColumnDragEnd(index, e)}
              onMouseEnter={(e) => {
                const stage = e.target.getStage();
                if (stage) stage.container().style.cursor = 'col-resize';
              }}
              onMouseLeave={(e) => {
                const stage = e.target.getStage();
                if (stage) stage.container().style.cursor = 'default';
              }}
            />
          );
        })}

      {/* Row Resize Handles */}
      {!previewMode &&
        Array.from({ length: numRows - 1 }).map((_, index) => {
          const handleYPos = cumulativeRowY[index + 1] - resizeHandleHeight / 2;
          return (
            <Rect
              key={`row-resize-handle-${index}`}
              x={0}
              y={handleYPos}
              width={totalCalculatedWidth} // Full table width for the handle
              height={resizeHandleHeight}
              fill={
                draggingRowIndex === index
                  ? 'rgba(0, 100, 255, 0.3)'
                  : 'rgba(0, 0, 255, 0.01)'
              }
              draggable
              dragBoundFunc={function (this: Node, pos) {
                const parentNode = this.parent;
                if (!parentNode) return pos;

                const parentAbsolutePos = parentNode.getAbsolutePosition();
                const parentScaleY = parentNode.getAbsoluteScale().y;
                const handleLocalHeight = this.height(); // Local, unscaled height of the handle

                // Min/Max stage Y for the handle's TOP EDGE based on row minimum heights
                // Handle 'index' is between row 'index' and 'index+1'
                // The handle's center is conceptually at cumulativeRowY[index + 1]
                // Its local top edge is at cumulativeRowY[index + 1] - handleLocalHeight / 2

                // Constraint from upper row (row 'index')
                // Lowest position for (cumulativeRowY[index + 1]) is (cumulativeRowY[index] + MIN_ROW_HEIGHT)
                const minHandleStageY_dueToRows =
                  parentAbsolutePos.y +
                  (cumulativeRowY[index] +
                    MIN_ROW_HEIGHT -
                    handleLocalHeight / 2) *
                    parentScaleY;

                // Constraint from lower row (row 'index+1')
                // Highest position for (cumulativeRowY[index + 1]) is (cumulativeRowY[index+2] - MIN_ROW_HEIGHT)
                const maxHandleStageY_dueToRows =
                  parentAbsolutePos.y +
                  (cumulativeRowY[index + 2] -
                    MIN_ROW_HEIGHT -
                    handleLocalHeight / 2) *
                    parentScaleY;

                // Clamp proposed pos.y by row constraints first
                let newY = Math.max(minHandleStageY_dueToRows, pos.y);
                newY = Math.min(newY, maxHandleStageY_dueToRows);

                // Overall table boundaries for the handle's TOP EDGE
                const overallMinHandleStageY = parentAbsolutePos.y; // Cannot go above table's top edge
                const overallMaxHandleStageY =
                  parentAbsolutePos.y +
                  (totalCalculatedHeight - handleLocalHeight) * parentScaleY; // Cannot go below table's bottom edge

                // Further clamp by overall table boundaries
                newY = Math.max(overallMinHandleStageY, newY);
                newY = Math.min(newY, overallMaxHandleStageY);

                return {
                  x: parentAbsolutePos.x, // Lock X to parent's absolute x
                  y: newY,
                };
              }}
              onDragStart={(e) => handleRowDragStart(index, e)}
              onDragMove={(e) => handleRowDragMove(index, e)}
              onDragEnd={(e) => handleRowDragEnd(index, e)}
              onMouseEnter={(e) => {
                const stage = e.target.getStage();
                if (stage) stage.container().style.cursor = 'row-resize';
              }}
              onMouseLeave={(e) => {
                const stage = e.target.getStage();
                if (stage) stage.container().style.cursor = 'default';
              }}
            />
          );
        })}

      {/* Render Outer Border */}
      <Rect
        width={totalCalculatedWidth}
        height={totalCalculatedHeight}
        stroke={borderColor}
        strokeWidth={borderWidth}
        listening={false}
      />
    </Group>
  );
});

export default AdvancedTableElement;
